import React, { useEffect, useState } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { green } from "@material-ui/core/colors";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: { display: "flex", flexWrap: "wrap" },
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  hint: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 4,
    marginBottom: 8,
  },
  systemNotice: {
    background: theme.palette.type === "dark" ? "rgba(99,102,241,0.1)" : "#eef2ff",
    border: theme.palette.type === "dark" ? "1px solid rgba(99,102,241,0.3)" : "1px solid #c7d2fe",
    color: theme.palette.type === "dark" ? "#c7d2fe" : "#4338ca",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 12,
    marginBottom: 12,
  },
}));

const SectionSchema = Yup.object().shape({
  name: Yup.string().trim().min(1, "Required").max(80, "Too long").required("Required"),
});

/**
 * Create / edit modal for a ticket section.
 *
 * - For built-in (`system`) sections only the display name is editable —
 *   the underlying status routing must stay intact.
 * - For custom (`tag-filter`) sections the user can pick any subset of
 *   their existing tags; tickets carrying ANY of these tags will appear
 *   in the section.
 */
const TicketSectionModal = ({ open, onClose, section, onSaved }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isEdit = Boolean(section && section.id);
  const isSystem = isEdit && section.type === "system";

  const [tags, setTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [initial, setInitial] = useState({ name: "", tagIds: [], color: "" });

  useEffect(() => {
    if (!open) return;
    setInitial({
      name: section?.name || "",
      tagIds: Array.isArray(section?.tagIds) ? section.tagIds : [],
      color: section?.color || "",
    });
  }, [open, section]);

  useEffect(() => {
    if (!open || isSystem) return;
    const load = async () => {
      try {
        setLoadingTags(true);
        const { data } = await api.get("/tags/list");
        setTags(Array.isArray(data) ? data : data?.tags || []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingTags(false);
      }
    };
    load();
  }, [open, isSystem]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        name: values.name.trim(),
        color: values.color || null,
      };
      if (!isSystem) payload.tagIds = values.tagIds;

      let saved;
      if (isEdit) {
        const { data } = await api.put(`/ticket-sections/${section.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post(`/ticket-sections`, payload);
        saved = data;
      }
      toast.success(isEdit ? "Section updated" : "Section created");
      onSaved && onSaved(saved);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedTagObjects = tags.filter(t => initial.tagIds.includes(t.id));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        {isEdit ? "Edit Section" : "Add Section"}
      </DialogTitle>

      <Formik
        enableReinitialize
        initialValues={initial}
        validationSchema={SectionSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting, touched, errors }) => (
          <Form>
            <DialogContent dividers>
              {isSystem && (
                <div className={classes.systemNotice}>
                  This is a built-in tab — only its display name can be changed.
                </div>
              )}

              <Field
                as={TextField}
                name="name"
                label="Section name"
                placeholder="e.g. Closed Deals, VIP Clients, Service"
                fullWidth
                margin="dense"
                variant="outlined"
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />

              {!isSystem && (
                <>
                  <div className={classes.hint}>
                    Tickets that carry ANY of these tags will appear here automatically.
                  </div>
                  <Autocomplete
                    multiple
                    size="small"
                    options={tags}
                    loading={loadingTags}
                    getOptionLabel={(o) => o.name || ""}
                    value={tags.filter(t => values.tagIds.includes(t.id))}
                    onChange={(_, value) =>
                      setFieldValue("tagIds", value.map(v => v.id))
                    }
                    defaultValue={selectedTagObjects}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option.id}
                          label={option.name}
                          size="small"
                          style={{
                            backgroundColor: option.color || undefined,
                            color: option.color ? "#fff" : undefined,
                          }}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Tags"
                        placeholder="Pick tags"
                        margin="dense"
                      />
                    )}
                  />
                </>
              )}

              <Field
                as={TextField}
                name="color"
                label="Color (optional, hex)"
                placeholder="#25d366"
                fullWidth
                margin="dense"
                variant="outlined"
              />
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} color="secondary" variant="outlined" disabled={isSubmitting}>
                Cancel
              </Button>
              <div className={classes.btnWrapper}>
                <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
                  {isEdit ? "Save" : "Create"}
                </Button>
                {isSubmitting && (
                  <CircularProgress size={24} className={classes.buttonProgress} />
                )}
              </div>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default TicketSectionModal;
